<template>
    <div>
        <ChangeUserEmailModal
            v-if="user"
            ref="changeUserEmailModal"
            :user="user"
            @update="handleEmailUpdated"
        />
        <div class="vx-card p-8 mt-8">
            <h4 class="mb-4">Actions</h4>
            <div class="flex flex-wrap items-center gap-4">
                <button class="sk-btn sk-btn-primary rounded-lg" @click="handleChangeEmail">
                    {{ $t('admin.users.actions.change_email') }}
                </button>
                <button class="sk-btn sk-btn-primary rounded-lg" @click="handleResendRestorePasswordEmail">
                    {{ $t('admin.users.actions.resend_restore_password_email') }}
                </button>
                <button v-if="canValidateEmail" class="sk-btn sk-btn-primary rounded-lg" @click="handleValidateEmail">
                    {{ $t('admin.users.actions.validate_email') }}
                </button>
            </div>
        </div>
        <div class="vx-card p-8 mt-8">
            <img :src="user.avatar ? user.avatar : 'https://userdatacf.skitude.com/images/photo_profile.png'" class="w-32 h-32 rounded-full mb-4">
            <div class="flex flex-row gap-2">
               <p class="font-bold">{{ $t('shops.username') }}:</p>
               <p>{{ user.username }}</p>
            </div>
            <div class="flex flex-row gap-2">
                <p class="font-bold">{{ $t('shops.id') }}:</p>
                <p>{{ user.id }}</p>
            </div>
            <div class="flex flex-row gap-2">
                <p class="font-bold">Uuid:</p>
                <p>{{ user.uuid }}</p>
            </div>
            <div class="flex flex-row gap-2">
                <p class="font-bold">{{ $t('shops.email') }}:</p>
                <p>{{ user.email }}</p>
            </div>
            <div class="flex flex-row gap-2">
                <p class="font-bold">{{ $t('admin.users.form.firstname') }}:</p>
                <p>{{ user.firstname ? user.firstname : '-' }}</p>
            </div>
            <div class="flex flex-row gap-2">
                <p class="font-bold">{{ $t('admin.users.form.lastname') }}:</p>
                <p>{{ user.lastname ? user.lastname : '-' }}</p>
            </div>
            <div class="flex flex-row gap-2">
                <p class="font-bold">{{ $t('admin.challenges.settings.community') }}:</p>
                <p>{{ communityName }}</p>
            </div>
            <div class="flex flex-row gap-2">
                <p class="font-bold">{{ $t('apps.registered-users.language') }}:</p>
                <p>{{ user.language }}</p>
            </div>
        </div>
        <div class="vx-card p-8 mt-8">
            <h4 class="mb-8">{{ $t('admin.photos.applications') }}</h4>
            <div class="flex flex-row gap-2">
                <p class="font-bold">{{ $t('admin.users.form.registered_at') }}:</p>
                <p>{{ user.registered_app_id ? getApplicationName(user.registered_app_id) : '-' }}</p>
            </div>
            <div v-if="user.app_logins.length > 0">
                <p class="font-bold">{{ $t('admin.users.form.logins') }}</p>
                <table>
                    <tbody>
                        <tr v-for="app in user.app_logins" :key="app.app_id">
                            <td class="py-2">{{ getApplicationName(app.app_id) }}</td>
                            <td class="py-2 px-2">{{ fromDBToTimezone(app.time, AppActiveUser.timezone).toLocaleString(DateTime.DATETIME_MED) }}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    </div>
</template>

<script>
import store from "@/modules/Shared/Store/store";
import UsersService from "@/modules/Admin/Services/UsersService";
import {unifyPaginatedResponse} from "@/modules/Shared/Helpers/apiResponseHelper";
import CommunityService from "@/modules/Admin/Services/CommunityService";
import ChangeUserEmailModal from "@/modules/Admin/Components/Users/ChangeUserEmailModal.vue";
import loader from "@/modules/Shared/Mixins/loader";
import PaginationService from "@/modules/Shared/Services/PaginationService";
import ApplicationService from "@/modules/Shared/Services/ApplicationService";
import {fromDBToTimezone} from "@/modules/Shared/Helpers/dateHelper";
import {DateTime} from "luxon";
import {mapGetters} from "vuex";

export default {
    name: "ClientUserDetailPage",
    components: {ChangeUserEmailModal},
    mixins: [loader],

    data() {
        return {
            user: null,
            communities: [],
            applicationsHashMap: {}
        }
    },
    async beforeRouteEnter(to, from, next) {
        const user = await UsersService.getUser(to.params.uuid);

        user.app_logins.reverse()

        const appIds = user.app_logins.map(app => app.app_id)
        if (user.registered_app_id) {
            appIds.push(user.registered_app_id)
        }

        const [communities, applications] = await Promise.all([
            unifyPaginatedResponse(CommunityService.getCommunities),
            unifyPaginatedResponse(ApplicationService.getApplications, PaginationService.parseParameters({
                per_page: 30,
                filters: [
                    { field: 'id', value: user.app_logins.map(app => app.app_id).join(';') },
                ]
            })),
        ])

        const applicationsHashMap = Object.fromEntries(
            applications.map(({ id, name }) => [id, name])
        );

        await store.dispatch('setRouteElement', user)
        next(vm => {
            vm.user = user
            vm.communities = communities
            vm.applicationsHashMap = applicationsHashMap
        })
    },
    computed: {
        ...mapGetters(['AppActiveUser']),
        DateTime() {
            return DateTime
        },
        userCommunity() {
            return this.communities.find(c => c.uuid === this.user.community_uuid)
        },
        communityName() {
            return this.userCommunity.name
        },
        canValidateEmail() {
            return !!this.userCommunity
                && !this.userCommunity.auto_validate_users
                && !this.user.email_validated
        }
    },
    methods: {
        fromDBToTimezone,
        getApplicationName(appId) {
            return this.applicationsHashMap[appId]
        },
        async handleEmailUpdated() {
            this.user = await UsersService.getUser(this.$route.params.uuid)
        },
        async handleChangeEmail() {
            await this.$refs.changeUserEmailModal.open()
        },
        async handleResendRestorePasswordEmail() {
            await this.loadAndNotify(async () => {
                await UsersService.sendEmail(this.$route.params.uuid, 'restore_password')
            })
        },
        async handleValidateEmail() {
            await this.loadAndNotify(async () => {
                // The user edit endpoint marks several unrelated fields as required, so we send
                // the full user object we already hold alongside the validation flag.
                await UsersService.editUser(this.user.uuid, { ...this.user, email_validated: true })
                this.user = { ...this.user, email_validated: true }
            })
        },
    }
}
</script>
